"use client";

import React from "react";
import Layout from "@/src/components/layout";
import CommonStringParser from "@/src/shared/components/CommonStringParser";
import { AboutSkeleton, useCompanyDetailsQuery } from "@/src/features/company";
import Footer from "@/src/components/layout/footer";
import SEO from "@/src/shared/components/SEO";
import { APP_URL } from "@/src/core/config/app";

const TermsConditionsView: React.FC = () => {
  const { data, isLoading, error, refetch } = useCompanyDetailsQuery();

  // SEO meta used across normal and error states
  const pageTitle =
    "Terms & Conditions — Travela | Your Trusted Travel Partner";
  const pageDescription =
    "Review Travela's Terms & Conditions: booking rules, cancellations & refunds, payment terms, host/guest responsibilities, and dispute resolution. Read before you book.";
  const canonicalUrl = `${APP_URL}/terms-conditions`;
  const pageUrl = `${APP_URL}/terms-conditions`;
  if (error) {
    return (
      <>
        <SEO
          title={pageTitle}
          description={pageDescription}
          canonical={canonicalUrl}
          url={pageUrl}
        />
        <Layout>
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md mx-4">
              <h2 className="text-xl font-semibold text-ink-black mb-2">
                Unable to load
              </h2>
              <p className="text-ink-secondary mb-4">
                We couldn't fetch the terms and conditions. Please try again.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => refetch()}
                  className="px-4 py-2 bg-coral-pink-500 text-white rounded-lg hover:bg-coral-pink-400 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </Layout>
      </>
    );
  }

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        url={pageUrl}
      />

      <Layout>
        <div className="hidden md:block">
          {isLoading ? (
            <AboutSkeleton />
          ) : (
            <div className="max-w-screen-lg mx-auto px-6 py-20">
              <header className="mb-8 text-center">
                <h1 className="text-3xl font-bold text-ink-black">
                  Terms & Conditions
                </h1>
                <p className="text-ink-secondary mt-2">
                  Please read these terms carefully before using our service.
                </p>
              </header>
              <article className="prose prose-lg max-w-none text-ink-dark">
                <CommonStringParser
                  dangerHtml={data?.terms_and_conditions || ""}
                />
              </article>
            </div>
          )}
        </div>

        <div className="md:hidden mt-12 p-4 px-8 mx-2">
          {isLoading ? (
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                <div className="h-4 bg-gray-200 rounded w-4/5"></div>
              </div>
            </div>
          ) : (
            <div className="prose prose-sm max-w-none text-ink-dark">
              <CommonStringParser
                dangerHtml={data?.terms_and_conditions || ""}
              />
            </div>
          )}
        </div>

        <Footer />
      </Layout>
    </>
  );
};

export default TermsConditionsView;
